import React from 'react';
import './ProgressIndicator.css';

/**
 * Progress Indicator Component for SSE Updates
 * Shows real-time progress of AI processing
 */
const ProgressIndicator = ({ 
  step, 
  message, 
  percentage = 0, 
  substep, 
  estimatedTime,
  type = 'progress',
  className = ''
}) => {
  // Determine the color based on progress
  const getProgressColor = () => {
    if (type === 'error') return '#ef4444';
    if (type === 'complete') return '#10b981';
    if (percentage < 30) return '#3b82f6';
    if (percentage < 60) return '#8b5cf6';
    if (percentage < 90) return '#06b6d4';
    return '#10b981';
  };

  // Format estimated time
  const formatTime = (seconds) => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className={`progress-indicator ${type} ${className}`}>
      <div className="progress-header">
        <div className="progress-step">
          {type === 'error' && <span className="progress-icon">❌</span>}
          {type === 'complete' && <span className="progress-icon">✅</span>}
          {type === 'progress' && percentage > 0 && (
            <span className="progress-icon">⏳</span>
          )}
          <span className="progress-step-text">{step}</span>
        </div>
        {percentage > 0 && (
          <div className="progress-percentage">
            {percentage}%
          </div>
        )}
      </div>

      {message && (
        <div className="progress-message">
          {message}
        </div>
      )}

      {substep && (
        <div className="progress-substep">
          💡 {substep}
        </div>
      )}

      {estimatedTime && (
        <div className="progress-time">
          ⏱️ Estimated time: {formatTime(estimatedTime)}
        </div>
      )}

      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill"
          style={{ 
            width: `${Math.min(100, Math.max(0, percentage))}%`,
            backgroundColor: getProgressColor()
          }}
        >
          <div className="progress-bar-shimmer"></div>
        </div>
      </div>

      {/* Animated dots for ongoing progress */}
      {type === 'progress' && percentage < 100 && (
        <div className="progress-dots">
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
      )}
    </div>
  );
};

/**
 * Compact Progress Indicator for Mobile
 */
export const CompactProgressIndicator = ({ 
  step, 
  percentage = 0,
  type = 'progress' 
}) => {
  const getProgressColor = () => {
    if (type === 'error') return '#ef4444';
    if (type === 'complete') return '#10b981';
    return '#3b82f6';
  };

  return (
    <div className="compact-progress-indicator">
      <div className="compact-progress-text">
        {step}
      </div>
      <div className="compact-progress-bar">
        <div 
          className="compact-progress-fill"
          style={{ 
            width: `${Math.min(100, Math.max(0, percentage))}%`,
            backgroundColor: getProgressColor()
          }}
        />
      </div>
    </div>
  );
};

/**
 * Progress Steps List
 * Shows all steps with their status
 */
export const ProgressStepsList = ({ steps = [] }) => {
  return (
    <div className="progress-steps-list">
      {steps.map((step, index) => (
        <div 
          key={index} 
          className={`progress-step-item ${step.status}`}
        >
          <div className="step-icon">
            {step.status === 'completed' && '✅'}
            {step.status === 'active' && '⏳'}
            {step.status === 'pending' && '⭕'}
            {step.status === 'error' && '❌'}
          </div>
          <div className="step-content">
            <div className="step-title">{step.title}</div>
            {step.description && (
              <div className="step-description">{step.description}</div>
            )}
          </div>
          {step.duration && (
            <div className="step-duration">{step.duration}ms</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProgressIndicator;